import { FormattedLocales } from "@/src/shared/types/locales";
import { AnyObject } from "@/src/shared/types/any";
import { CustomValue } from "@/src/shared/types/schemaTypes.generated";
import { RuleObject } from "ant-design-vue/es/form/interface";

export const getNumberOfRowsForSkeleton = function (skeletonHeight: number): number {
  if (!skeletonHeight || typeof skeletonHeight !== "number") return 0;
  const oneSkeletonRowHeightInPx = 32;
  const numberOfRows = Math.ceil(skeletonHeight / oneSkeletonRowHeightInPx);
  return numberOfRows;
};

export const requiredNumberValidator = function (rule: RuleObject, value: string) {
  if (!value) {
    return Promise.reject(rule.message);
  }
  return Promise.resolve();
};

/**
 * Builds custom fields for a chosen DOM element (container).
 * Container - should be a reference to an HTML <form> tag.
 * Classes - additional classes to be added.
 * @param options
 */
export const customFieldsBuilder = function (options: {
  cfArray: CustomValue[];
  container: string;
  classes: {
    wrapper?: string;
    label?: string;
  };
}): void {
  const { cfArray, container, classes } = options;
  if (!cfArray.length) return;
  classes.wrapper = classes.wrapper ? classes.wrapper : "";
  classes.label = classes.label ? classes.label : "";
  let cfInnerHtml = "";
  const cfContainer = document.querySelector(container) as HTMLElement;
  if (!cfContainer) return;
  cfArray.forEach(cf => {
    const req = cf.customField.isRequired;
    cfInnerHtml += `
      <div class="vue-modal__cf-form-item ${classes.wrapper}">
        <label class="vue-modal__cf-form-item-label ${classes.label} ${req ? "cf-required" : ""}">
          ${cf.customField.name} ${req ? "*" : ""}
        </label>
        <div>${cf.editTag}</div>
      </div>
    `;
  });
  cfContainer.innerHTML = cfInnerHtml;
};

/**
 * Method to get a values from cf form
 * cfForm - a ref object of a form
 * cfPrefix - same as for graphQl query for cf
 * Will return an object with cf names in a right format for sending to server and their values
 * @param options
 */
export const getCFValues = function (options: {
  cfArray: { customField: { id: string } }[];
  cfForm: HTMLFormElement;
  cfPrefix: string;
}): { [key: string]: FormDataEntryValue | null } {
  const { cfArray, cfForm, cfPrefix } = options;
  const formData = new FormData(cfForm);
  const cfValues: { [key: string]: FormDataEntryValue | null } = {};
  cfArray.forEach(cf => {
    const name = `${cfPrefix}[custom_field_values][${cf.customField.id}]`;
    const value = formData.get(name);
    cfValues[cf.customField.id] = value;
  });
  return cfValues;
};

export const getValue = function (eventValue: any) {
  let value: any;
  if (Object.hasOwnProperty.call(eventValue.inputValue, "value")) {
    value = eventValue.inputValue.value;
  } else {
    value = eventValue.inputValue;
  }
  return value;
};

export const getTotalRatio = function (done: number, all: number): number | null {
  if (!all) return null;
  const ratio: number = Math.round((done / all) * 100);
  return ratio;
};

export const humanizeHashKeyValue = function (
  property: { key: string; value: string }[]
): { id: string; name: string }[] {
  const humanized = [...property];
  if (!humanized.length) return [];
  return humanized.map(prop => {
    return { id: prop.key, name: prop.value };
  });
};

const getOldModals = function (): HTMLElement[] {
  return Array.from(document.querySelectorAll(".ui-dialog.modal")) as HTMLElement[];
};

export const hasActiveOldModal = function (): boolean {
  return getOldModals().some(modal => getComputedStyle(modal).display !== "none");
};

/**
 * if a new vue modal is opened from an old modal, we need to hide/show the old modals
 * in order to interact with the input fields of the new modal.
 * @param displayStyle
 */
export const setOldModalsStyle = function (displayStyle: string): void {
  const oldModals = getOldModals();
  if (!oldModals.length) return;
  oldModals.forEach(modal => {
    modal.style.display = displayStyle;
  });
};

export const prepareLabelClass = function ({
  bem,
  block,
  required,
}: {
  bem: {
    block: string;
    ify: (block: string, element: string, modifier?: string) => string;
  };
  block: string;
  required?: boolean;
}): string {
  const requiredClass = required ? "required" : "";
  return bem.ify(block, "form-item-label", requiredClass);
};

export const priceFormat = function (value: number, currency: string, translations: FormattedLocales): string {
  const precision = 2;
  const delimiter: string = translations?.["number.currency.format.delimiter"] || ".";
  const separator: string = translations?.["number.currency.format.separator"] || ",";
  const regex = new RegExp(`\\d(?=(\\d{3})+${precision > 0 ? "\\D" : "$"})`, "g");
  const formatedNumber: string = Number(value)
    .toFixed(Math.max(0, Math.floor(precision)))
    .toString();
  const replacedDelimiter: string = formatedNumber.replace(".", delimiter);
  const formatedPrice: string = replacedDelimiter.replace(regex, `$&${separator}`);
  return `${formatedPrice}  ${currency}`;
};

export const changeModalState = function (entityType: string, state: string, stateParams?: AnyObject): void {
  if ((window as any).EasyVue && (window as any).EasyVue.changeModalState) {
    (window as any).EasyVue.changeModalState(entityType, state, stateParams);
  }
};

export const setEstimatedHours = function (setLevel: AnyObject, value: number): void {
  const oldValue = setLevel["estimatedHours"];
  const difference = value - oldValue;
  setLevel["estimatedHours"] = value;
  setLevel["totalEstimatedHours"] = setLevel["totalEstimatedHours"] + difference;
};

export const calculateTopMenuHeight = function (): number {
  let topMenuHeight = (window as any).ERUI.sassData["topmenu-height"];
  const topMenuHeightInRem = topMenuHeight?.match("rem");
  topMenuHeight = parseFloat(topMenuHeight);
  // if top-menu height is in rem, we need to recalculate it to pixels
  if (topMenuHeightInRem) {
    const fontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    topMenuHeight = topMenuHeight * fontSize;
  }
  return topMenuHeight || 0;
};

export const convertBooleanStringToBoolean = (value: string): boolean => {
  return value === "1" ? true : false;
};
